import { unByKey } from 'ol/Observable';
import VectorEventType from 'ol/source/VectorEventType';
import { equals } from 'ol/coordinate';
import Undo from 'undo.js';

import { Observable, on } from 'cadenza/utils/event-util';
import { confirm, errorAlert } from 'cadenza/alert';
import { ClassicSketchApi } from 'cadenza/api-client/classic-sketch-api';
import { context } from 'cadenza/map/context/context';
import {
  ClassicSketchDrawInteractionGroup
} from 'cadenza/map/classic-sketch-layer/interactions/classic-sketch-draw-interaction-group';
import { LayerProperty } from 'cadenza/map/layer-property';
import { clearAndRefreshVectorLayer } from 'cadenza/map/layer-utils';
import { MapProperty } from 'cadenza/map/map-property';
import {
  ClassicSketchModifyInteractionGroup
} from 'cadenza/map/classic-sketch-layer/interactions/classic-sketch-modify-interaction-group';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { logger } from 'cadenza/utils/logging';
import { throttle } from 'cadenza/utils/throttle';
import { TranslateEventType } from 'cadenza/map/interactions/translate-event';
import { createStyleItemFromPaletteItem } from 'cadenza/map/classic-sketch-layer/sketch-style';
import { ModifyEventType } from 'cadenza/map/interactions/modify-interaction';
import { BringToFrontCommand } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/bring-to-front-command';
import { ChangeFeatureCommand } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/change-feature-command';
import {
  CreateFeaturesCommand
} from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/create-features-command';
import {
  GroupCommand,
  UngroupCommand
} from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/group-ungroup-commands';
import {
  RemoveFeaturesCommand
} from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/remove-features-command';
import { SendToBackCommand } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/commands/send-to-back-command';
import {
  DELETE_KEY_CODE,
  SketchSidebarContent
} from 'cadenza/map/classic-sketch-layer/sketch-sidebar/sketch-sidebar-content';
import i18n from 'cadenza/map/classic-sketch-layer/sketch-sidebar/sketch.properties';
import { hashCode } from 'cadenza/utils/hash-code';
import { SketchFormat } from 'cadenza/map/classic-sketch-layer/sketch-format';

const STANDARD_TOOLS_SKETCH_PALETTE_ID = 'STANDARD-TOOLS-SKETCH-PALETTE-ID';

export const SKETCH_EVENTS = {
  SELECTION_CHANGE: 'change:selection',
  FEATURE_CHANGE: 'change:features',
  UNDO_REDO_OPERATION: 'undoRedo'
};

export class Sketch extends Observable {

  #sketchSidebarContent;
  #map;
  #palettes = {};
  #sketchLayer;
  #undoStack;
  #sidebar;
  #eventKeys;
  #featureEventKeys;
  #previousPrimaryInteraction;
  #autoSavePromise;

  constructor (sketchLayer, map, sidebar) {
    super();

    this.#map = map;
    this.#sidebar = sidebar;

    this.save = throttle(this.save, 1000);

    this._onKeyDownHandler = event => this._onKeyDown(event);

    this.#sketchSidebarContent = new SketchSidebarContent(this, {
      onPaletteItemSelect: (paletteId, itemIdx) => this.onPaletteItemSelect(paletteId, itemIdx),
      onPaletteItemsDeselect: () => this.onPaletteItemsDeselect()
    });

    on(
      this.#sketchSidebarContent,
      SKETCH_EVENTS.FEATURE_CHANGE,
      event => this.onFeaturesChanged(event.detail.features)
    );

    this.#map.updateSize();

    this.sketchLayer = sketchLayer;
    this.initPalettes();
  }

  initUndoStack () {
    this.#undoStack = new Undo.Stack();
    this.#undoStack.changed = () => this.#sidebar.sketchToolbar.updateButtons();
  }

  get canUndo () {
    return this.#undoStack.canUndo();
  }

  get canRedo () {
    return this.#undoStack.canRedo();
  }

  undo () {
    this.#undoStack.undo();
    this.save();
    this._dispatchUndoRedoEvent();
  }

  redo () {
    this.#undoStack.redo();
    this.save();
    this._dispatchUndoRedoEvent();
  }

  get sketchLayer () {
    return this.#sketchLayer;
  }

  set sketchLayer (sketchLayer) {
    if (sketchLayer === this.#sketchLayer) {
      return;
    }

    this.initUndoStack();

    if (this.#sketchLayer) {
      this.unregisterLayerEvents();
      this._unregisterFeatureEvents();
      this.#sketchLayer.setEditing(false);
    }

    this.#sketchLayer = sketchLayer;

    if (this.#sketchLayer) {
      this.#sketchLayer.setEditing(true);
      this.#previousPrimaryInteraction = context(this.#map).getCurrentPrimaryInteractionIfResettable();
      this.sketchLayer.enableModifyInteraction();
      this._registerFeatureEvents();
      this.registerLayerEvents();
      if (this.#sidebar.sketchToolbar) {
        this.#sidebar.sketchToolbar.updateButtons();
      }
      this.#sketchSidebarContent.deselectAllPaletteItems();
    } else {
      this.#map.set(MapProperty.PRIMARY_INTERACTION, this.#previousPrimaryInteraction);
    }

    this.#map.set(MapProperty.SKETCH_LAYER, sketchLayer);
    this.#map.set(MapProperty.EDITING_LAYER, !!sketchLayer);

    this._dispatchSelectionChangeEvent();
  }

  registerLayerEvents () {
    const vectorSource = this.#sketchLayer.getFeatureLayer().getSource();

    this.#eventKeys = [
      vectorSource.on('change:loadend', event => {
        if (event.oldValue === false) {
          this.onLoadEnd();
        }
      }),
      vectorSource.on(VectorEventType.ADDFEATURE, event => {
        if (!this.#sketchLayer.isFeaturesLoading() && !this.#sketchLayer.isSilentAdd()) {
          this.onFeaturesCreated(event.feature);
        }
      }),
      this.#map.getLayers().on('remove', event => {
        if (event.element === this.#sketchLayer) {
          this.cancelEditing();
        }
      }),
      this.#map.on(`change:${MapProperty.SELECTED_LAYER}`, () => {
        if (this.#map.get(MapProperty.SELECTED_LAYER) !== this.sketchLayer) {
          this.sketchLayer = undefined;
        }
      }),
      this.#map.on(`change:${MapProperty.PRIMARY_INTERACTION}`, () => {
        const primaryInteraction = this.#map.get(MapProperty.PRIMARY_INTERACTION);
        if (!primaryInteraction) {
          this._enableSelectTool();
        } else if (!(primaryInteraction instanceof ClassicSketchModifyInteractionGroup)
          && !(primaryInteraction instanceof ClassicSketchDrawInteractionGroup)) {
          this.#sketchSidebarContent.deselectAllPaletteItems();
          this.#sidebar.sketchToolbar.setSelectToolActive(false);
          this.#previousPrimaryInteraction = context(this.#map).getCurrentPrimaryInteractionIfResettable() || this.#previousPrimaryInteraction;
        }
      })
    ];
  }

  _registerFeatureEvents () {
    const selectedFeaturesCollection = this.#sketchLayer.getSelectedFeatures();
    const modifyInteraction = this.#sketchLayer.getModifyInteraction();
    const translateInteraction = this.#sketchLayer.getTranslateInteraction();
    const translateByHandleInteraction = this.#sketchLayer.getTranslateByHandleInteraction();

    this._unregisterFeatureEvents();
    this.#featureEventKeys = [
      selectedFeaturesCollection.on([ 'add', 'remove' ], () => {
        this.onChangeFeatureSelection(selectedFeaturesCollection.getArray());
      }),
      modifyInteraction.on(ModifyEventType.MODIFYEND, event => this.onFeaturesChanged(event.features.getArray())),
      translateInteraction.on(TranslateEventType.TRANSLATE_START, event => this._onFeaturesTranslateStart(event)),
      translateInteraction.on(TranslateEventType.TRANSLATE_END, event => this._onFeaturesTranslateEnd(event)),
      translateByHandleInteraction.on(TranslateEventType.TRANSLATE_START, event => this._onFeaturesTranslateStart(event)),
      translateByHandleInteraction.on(TranslateEventType.TRANSLATE_END, event => this._onFeaturesTranslateEnd(event))
    ];
    document.addEventListener('keydown', this._onKeyDownHandler);
  }

  _unregisterFeatureEvents () {
    unByKey(this.#featureEventKeys);
    document.removeEventListener('keydown', this._onKeyDownHandler);
  }

  unregisterLayerEvents () {
    unByKey(this.#eventKeys);
  }

  onChangeFeatureSelection (selectedFeatures) {
    this.#sidebar.sketchToolbar.updateButtons();
    this._dispatchSelectionChangeEvent();
  }

  initPalettes () {
    ClassicSketchApi.getPalettes().then((palettes) => {
      const userSpecificPalettes = palettes.filter(palette => palette.id !== STANDARD_TOOLS_SKETCH_PALETTE_ID);
      const hasStandardToolsPalette = userSpecificPalettes.length !== palettes.length;
      if (hasStandardToolsPalette) {
        this.fillStandardToolsPaletteContainer();
      }
      this.#sketchSidebarContent.addPaletteSection(userSpecificPalettes, id => this.selectPalette(id));
      this.selectPalette(userSpecificPalettes.length > 0 ? userSpecificPalettes[0].id : undefined);
    }).catch(err => logger.error('Error getting sketch palettes', err));
  }

  selectPalette (paletteId) {
    if (paletteId) {
      this.#sketchSidebarContent.deselectAllPaletteItems();
      this.getPalette(paletteId).then(palette => {
        const items = palette.items.map((item, itemIdx) => ({
          idx: itemIdx,
          imageSrc: item.previewImage,
          printName: item.printName
        }));
        this.#sketchSidebarContent.updatePaletteItems(paletteId, items);
      }).catch(err => {
        logger.error('Error getting sketch palette', err);
        this.#sketchSidebarContent.updatePaletteItems(paletteId, null);
      });
    }
  }

  getPalette (paletteId) {
    if (this.#palettes[paletteId]) {
      return Promise.resolve(this.#palettes[paletteId]);
    } else {
      return ClassicSketchApi.getPalette(paletteId).then((palette) => {
        this.#palettes[paletteId] = palette;
        palette.items.forEach(item => {
          item.paletteId = paletteId;
        });
        return palette;
      });
    }
  }

  onPaletteItemSelect (paletteId, itemIdx) {
    if (this.#palettes[paletteId] && itemIdx < this.#palettes[paletteId].items.length) {
      this.#sidebar.sketchToolbar.setSelectToolActive(false);
      this.#sketchLayer.unselectAllFeatures();
      this.#sketchLayer.enableDrawInteraction(this.#palettes[paletteId].items[itemIdx]);
    }
  }

  onPaletteItemsDeselect () {
    // do nothing if draw interaction was already turned off (for example FeatureInfo is already active)
    if (this.#map.get(MapProperty.PRIMARY_INTERACTION) instanceof ClassicSketchDrawInteractionGroup) {
      this._enableSelectTool();
    }
  }

  _enableSelectTool () {
    if (this.#sketchLayer) {
      this.#sidebar.sketchToolbar.setSelectToolActive(true);
      this.#sketchLayer.enableModifyInteraction();
      this._registerFeatureEvents();
    }
  }

  cancelEditing () {
    this.sketchLayer = undefined;
  }

  cancel () {
    if (this.#autoSavePromise) {
      this.#autoSavePromise
        .then(() => this.cancelEditing())
        .catch(err => logger.error('Could not cancel sketch layer editing', err));
    } else {
      this.cancelEditing();
    }
  }

  save () {
    /* eslint-disable promise/no-nesting */
    if (this.#autoSavePromise || this.#sketchLayer.isFeaturesLoading()) {
      this.saveScheduled = true;
      return;
    }
    this.#autoSavePromise = this.#sketchLayer.saveFeatures()
      .then(() => this.#undoStack.save())
      .catch(error => {
        const sketchLayer = this.#sketchLayer;
        if (error.key === 'update-in-database-repository-failure-missing-database-item') {
          errorAlert(i18n('autosave.error.deletedFromRepo.title'), i18n('autosave.error.deletedFromRepo.message'))
            .finally(() => sketchLayer.getFeatureLayer().setMap(null));
        } else if (error.status === 403) {
          errorAlert(i18n('autosave.error.insufficientPrivileges.title'), i18n('autosave.error.insufficientPrivileges.message'))
            .finally(() => {
              const privileges = sketchLayer.get(LayerProperty.PRIVILEGES);
              sketchLayer.set(LayerProperty.PRIVILEGES, privileges.filter(privilege => privilege !== 'write'));
              this.cancelEditing();
            });
        } else {
          const title = error.key === 'update-in-database-repository-failure-stale-state'
            ? i18n('autosave.error.staleState.title')
            : i18n('autosave.error.title');
          confirm(title, i18n('autosave.error.message'), {
            submit: i18n('autosave.error.reload'),
            alternativeSubmit: i18n('autosave.error.cancel'),
            cancel: false
          }).then(reloadConfirmed => {
            if (reloadConfirmed) {
              clearAndRefreshVectorLayer(sketchLayer.getFeatureLayer());
            } else {
              this.cancelEditing();
            }
          }).catch(err => logger.error('Could not reload sketch feature layer or cancel the editing ', err));
        }
      })
      .finally(() => {
        this.#autoSavePromise = null;
        this.onLoadEnd();
      });
    /* eslint-enable promise/no-nesting */
  }

  _onFeaturesTranslateStart (event) {
    event.features.forEach(feature => {
      feature.set(SketchFeatureProperty.OLD_GEOMETRY, feature.getGeometry().clone());
    });
  }

  _onFeaturesTranslateEnd (event) {
    const roundedCoordinatesBefore = event.startCoordinate.map((coordinate) => +(Math.round(coordinate)));
    const roundedCoordinatesAfter = event.coordinate.map((coordinate) => +(Math.round(coordinate)));
    if (!equals(roundedCoordinatesBefore, roundedCoordinatesAfter)) {
      this.onFeaturesChanged(event.features.getArray());
    }
  }

  onFeaturesRemoved (features) {
    this.#undoStack.execute(new RemoveFeaturesCommand(this.#sketchLayer, features));
    this._dispatchSelectionChangeEvent();
    this.save();
  }

  onFeaturesCreated (feature) {
    const paletteItem = this.#sketchLayer.getPaletteItem();
    feature.set(SketchFeatureProperty.PALETTE_ITEM_REFERENCE, {
      paletteId: paletteItem.paletteId,
      paletteItemId: paletteItem.id
    });
    // The feature SketchFeatureProperty.STYLE_ITEM may contain the "lines" property, this is not present on the paletteItem,
    // it is added in the draw-interaction::_finishTextDraw
    feature.set(SketchFeatureProperty.STYLE_ITEM, { ...feature.get(SketchFeatureProperty.STYLE_ITEM), ...createStyleItemFromPaletteItem(paletteItem) });
    const sketchItem = new SketchFormat().writeFeature(feature);
    feature.setId(hashCode(JSON.stringify(sketchItem)));
    this.#undoStack.execute(new CreateFeaturesCommand(this.#sketchLayer, [ feature ]));
    this.save();
  }

  onFeaturesChanged (features) {
    this.#undoStack.execute(new ChangeFeatureCommand(features));
    this.save();
  }

  onLoadEnd () {
    if (this.saveScheduled) {
      this.saveScheduled = false;
      this.save();
    }
  }

  deleteSelectedFeatures () {
    const selectedFeaturesCopy = this.#sketchLayer.getSelectedFeatures().getArray().slice();
    this.#sketchLayer.unselectAllFeatures();
    this.#sketchLayer.removeFeatures(selectedFeaturesCopy);
    this.onFeaturesRemoved(selectedFeaturesCopy);
  }

  bringSelectedFeaturesToFront () {
    const selectedFeatures = this.#sketchLayer.getSelectedFeatures().getArray();
    this.#undoStack.execute(new BringToFrontCommand(this.#sketchLayer, selectedFeatures));
    this.save();
  }

  sendSelectedFeaturesToBack () {
    const selectedFeatures = this.#sketchLayer.getSelectedFeatures().getArray();
    this.#undoStack.execute(new SendToBackCommand(this.#sketchLayer, selectedFeatures));
    this.save();
  }

  groupUngroupFeatures () {
    const selectedFeatures = this.#sketchLayer.getSelectedFeatures().getArray();
    const { groups, elements } = groupFeaturesByGroup(selectedFeatures);
    const command = elements.length
      ? new GroupCommand(this.#sketchLayer, elements)
      : new UngroupCommand(this.#sketchLayer, groups);
    this.#undoStack.execute(command);
    this.save();
  }

  startSelectingFeatures () {
    this.#sketchSidebarContent.deselectAllPaletteItems();
    this.#sidebar.sketchToolbar.setSelectToolActive(true);
    this.#sketchLayer.enableModifyInteraction();
    this._registerFeatureEvents();
  }

  _onKeyDown (event) {
    if (event.keyCode === DELETE_KEY_CODE && this.#sketchLayer.getSelectedFeatures().getArray().length) {
      this.deleteSelectedFeatures();
    }
  }

  getContent () {
    return this.#sketchSidebarContent;
  }

  fillStandardToolsPaletteContainer () {
    this.getPalette(STANDARD_TOOLS_SKETCH_PALETTE_ID).then(palette => {
      const items = palette.items.map((item, itemIdx) => ({
        idx: itemIdx,
        imageSrc: item.previewImage,
        printName: item.printName
      }));
      this.#sketchSidebarContent.updateStandardToolsPaletteItems(STANDARD_TOOLS_SKETCH_PALETTE_ID, items);
    }).catch(err => {
      logger.error('Error getting standard tools sketch palette', err);
    });
  }

  _dispatchSelectionChangeEvent () {
    if (this.#sketchLayer) {
      this.dispatchEvent(
        new CustomEvent(SKETCH_EVENTS.SELECTION_CHANGE, { detail: { features: this.#sketchLayer.getSelectedFeatures().getArray() } })
      );
    }
  }

  _dispatchUndoRedoEvent () {
    this.dispatchEvent(
      new CustomEvent(SKETCH_EVENTS.UNDO_REDO_OPERATION)
    );
  }

}

export function groupFeaturesByGroup (features) {
  const groups = {};
  const elements = [];
  for (const feature of features) {
    const group = feature.get(SketchFeatureProperty.GROUP);
    if (group) {
      const groupElements = groups[group] || [];
      groups[group] = groupElements;
      groupElements.push(feature);
    } else {
      elements.push(feature);
    }
  }
  return { groups, elements };
}
